import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Form, DatePicker, Select, Input, Space, Tag, Statistic, Row, Col, message } from 'antd';
import { DownloadOutlined, CalendarOutlined, FileTextOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import dayjs from 'dayjs';
import './AppointmentsReport.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const AppointmentsReport = () => {
    const [appointments, setAppointments] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const { t } = useTranslation();
    const [form] = Form.useForm();

    useEffect(() => {
        fetchAppointmentsReport();
    }, []);

    const fetchAppointmentsReport = async (filterValues = {}) => {
        setLoading(true);
        try {
            const params = {};

            if (filterValues.dateRange) {
                params.startDate = filterValues.dateRange[0].format('YYYY-MM-DD');
                params.endDate = filterValues.dateRange[1].format('YYYY-MM-DD');
            }

            if (filterValues.status) {
                params.status = filterValues.status;
            }

            if (filterValues.patientName) {
                params.patientName = filterValues.patientName;
            }

            console.log('Fetching report with params:', params);
            const response = await api.get('/report/appointments', { params });
            console.log('Report response:', response.data);

            if (response.data.success) {
                setAppointments(response.data.data.appointments || []);
                setStatistics(response.data.data.statistics || null);
                console.log('Appointments set:', response.data.data.appointments?.length || 0);
            } else {
                console.error('Response not successful:', response.data);
                setAppointments([]);
                setStatistics(null);
            }
        } catch (error) {
            console.error('Error fetching appointments report:', error);
            console.error('Error details:', error.response?.data);
            message.error(error.response?.data?.message || t('failedToFetchReport'));
            setAppointments([]);
            setStatistics(null);
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = (values) => {
        setFilters(values);
        fetchAppointmentsReport(values);
    };

    const handleReset = () => {
        form.resetFields();
        setFilters({});
        fetchAppointmentsReport();
    };

    const handleExport = () => {
        try {
            // Prepare CSV data
            const headers = [
                t('date'),
                t('time'),
                t('patientName'),
                t('phoneNumber'),
                t('age'),
                t('gender'),
                t('status'),
                t('checkInTime'),
                t('notes'),
                t('createdBy')
            ];

            const csvData = appointments.map(apt => [
                dayjs(apt.appointmentDate).format('YYYY-MM-DD'),
                apt.appointmentTime,
                apt.patientId?.name || '-',
                apt.patientId?.phoneNumber || '-',
                apt.patientId?.age || '-',
                apt.patientId?.gender || '-',
                t(apt.status),
                apt.checkInTime ? dayjs(apt.checkInTime).format('YYYY-MM-DD HH:mm') : '-',
                apt.notes || '-',
                apt.createdBy?.name || '-'
            ]);

            // Create CSV content
            const csvContent = [
                headers.join(','),
                ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
            ].join('\n');

            // Download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `appointments_report_${dayjs().format('YYYY-MM-DD')}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(t('reportExported'));
        } catch (error) {
            console.error('Error exporting report:', error);
            message.error(t('exportFailed'));
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            scheduled: 'blue',
            arrived: 'cyan',
            'in-consultation': 'orange',
            completed: 'green',
            cancelled: 'red',
            'no-show': 'gray'
        };
        return colors[status] || 'default';
    };

    const columns = [
        {
            title: t('date'),
            dataIndex: 'appointmentDate',
            key: 'appointmentDate',
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
            sorter: (a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate),
            width: 120
        },
        {
            title: t('time'),
            dataIndex: 'appointmentTime',
            key: 'appointmentTime',
            width: 100
        },
        {
            title: t('patientName'),
            dataIndex: ['patientId', 'name'],
            key: 'patientName',
            render: (name) => name || '-',
            width: 150
        },
        {
            title: t('phoneNumber'),
            dataIndex: ['patientId', 'phoneNumber'],
            key: 'phoneNumber',
            render: (phone) => phone || '-',
            width: 130
        },
        {
            title: t('age'),
            dataIndex: ['patientId', 'age'],
            key: 'age',
            render: (age) => age || '-',
            width: 70
        },
        {
            title: t('gender'),
            dataIndex: ['patientId', 'gender'],
            key: 'gender',
            render: (gender) => gender ? t(gender) : '-',
            width: 90
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {t(status)}
                </Tag>
            ),
            width: 130
        },
        {
            title: t('checkInTime'),
            dataIndex: 'checkInTime',
            key: 'checkInTime',
            render: (time) => time ? dayjs(time).format('HH:mm') : '-',
            width: 100
        },
        {
            title: t('notes'),
            dataIndex: 'notes',
            key: 'notes',
            render: (notes) => notes || '-',
            ellipsis: true
        },
        {
            title: t('createdBy'),
            dataIndex: ['createdBy', 'name'],
            key: 'createdBy',
            render: (name) => name || '-',
            width: 120
        }
    ];

    return (
        <div className="appointments-report">
            <Card
                title={
                    <Space>
                        <FileTextOutlined />
                        {t('appointmentsReport')}
                    </Space>
                }
                extra={
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleExport}
                        disabled={appointments.length === 0}
                    >
                        {t('exportReport')}
                    </Button>
                }
            >
                {/* Statistics */}
                {statistics && (
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Card>
                                <Statistic
                                    title={t('totalAppointments')}
                                    value={statistics.total}
                                    valueStyle={{ color: '#1890ff' }}
                                    prefix={<CalendarOutlined />}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Card>
                                <Statistic
                                    title={t('scheduled')}
                                    value={statistics.scheduled}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Card>
                                <Statistic
                                    title={t('completed')}
                                    value={statistics.completed}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Card>
                                <Statistic
                                    title={t('arrived')}
                                    value={statistics.arrived}
                                    valueStyle={{ color: '#13c2c2' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Card>
                                <Statistic
                                    title={t('cancelled')}
                                    value={statistics.cancelled}
                                    valueStyle={{ color: '#ff4d4f' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Card>
                                <Statistic
                                    title={t('noShow')}
                                    value={statistics.noShow}
                                    valueStyle={{ color: '#8c8c8c' }}
                                />
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Filters */}
                <Card style={{ marginBottom: 16 }} size="small">
                    <Form
                        form={form}
                        layout="inline"
                        onFinish={handleFilter}
                        style={{ gap: 8 }}
                    >
                        <Form.Item name="dateRange" style={{ marginBottom: 8 }}>
                            <RangePicker
                                format="YYYY-MM-DD"
                                placeholder={[t('startDate'), t('endDate')]}
                                style={{ width: 250 }}
                            />
                        </Form.Item>

                        <Form.Item name="status" style={{ marginBottom: 8 }}>
                            <Select
                                placeholder={t('selectStatus')}
                                allowClear
                                style={{ width: 150 }}
                            >
                                <Option value="all">{t('all')}</Option>
                                <Option value="scheduled">{t('scheduled')}</Option>
                                <Option value="arrived">{t('arrived')}</Option>
                                <Option value="in-consultation">{t('in-consultation')}</Option>
                                <Option value="completed">{t('completed')}</Option>
                                <Option value="cancelled">{t('cancelled')}</Option>
                                <Option value="no-show">{t('no-show')}</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item name="patientName" style={{ marginBottom: 8 }}>
                            <Input
                                placeholder={t('searchByPatientName')}
                                prefix={<SearchOutlined />}
                                style={{ width: 200 }}
                            />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 8 }}>
                            <Space>
                                <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                                    {t('search')}
                                </Button>
                                <Button onClick={handleReset} icon={<ReloadOutlined />}>
                                    {t('reset')}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Card>

                {/* Table */}
                <Table
                    columns={columns}
                    dataSource={appointments}
                    loading={loading}
                    rowKey="_id"
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showTotal: (total) => `${t('total')} ${total} ${t('appointments').toLowerCase()}`,
                        pageSizeOptions: ['10', '20', '50', '100']
                    }}
                    scroll={{ x: 1200 }}
                />
            </Card>
        </div>
    );
};

export default AppointmentsReport;
